export const API = process.env.REACT_APP_API_URL || "http://localhost:4000";

/**
 * parseDBDate — converte timestamps do SQLite para Date corretamente.
 *
 * SQLite armazena em UTC sem indicador de fuso ("YYYY-MM-DD HH:MM:SS").
 * O construtor Date() do JS trata strings sem fuso como hora LOCAL, não UTC,
 * o que causa horários 3h à frente no Brasil (UTC-3).
 * Este helper normaliza para ISO-8601 UTC antes de parsear.
 */
export function parseDBDate(dateText) {
  const s = String(dateText || "").trim();
  if (!s) return new Date(NaN);
  // Se já tem info de fuso (Z, +HH:MM, -HH:MM), parseia direto
  if (/Z$|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s);
  // Formato SQLite "YYYY-MM-DD HH:MM:SS[.SSS]" → ISO UTC com Z
  return new Date(s.replace(" ", "T") + "Z");
}

// Helpers de sessão — usar sessionStorage em vez de localStorage
// para que o token seja limpo automaticamente ao fechar o browser,
// reduzindo a janela de exposição a ataques XSS.
// Para segurança máxima em produção, migrar para httpOnly cookies.
export const session = {
  getToken: () => sessionStorage.getItem("safezap_token") || "",
  getUser: () => { try { const s = sessionStorage.getItem("safezap_user"); return s ? JSON.parse(s) : null; } catch { return null; } },
  save: (token, user) => { sessionStorage.setItem("safezap_token", token); sessionStorage.setItem("safezap_user", JSON.stringify(user)); },
  clear: () => { sessionStorage.removeItem("safezap_token"); sessionStorage.removeItem("safezap_user"); },
};

export function formatTime(dateText) {
  if (!dateText) return "";
  try {
    return parseDBDate(dateText).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export function formatMoney(value) {
  return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function formatDateTimeFull(dateText) {
  if (!dateText) return "";
  try {
    return parseDBDate(dateText).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export function normalizeBrazilPhone(value = "") {
  let digits = String(value).replace(/\D/g, "").replace(/^0+/, "");
  if (!digits) return "";
  if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) return digits;
  if (digits.length === 10 || digits.length === 11) return `55${digits}`;
  return digits;
}

export function formatBrazilPhone(value = "") {
  const digits = normalizeBrazilPhone(value);
  if (!digits) return "";

  const cc = digits.slice(0, 2);
  const ddd = digits.slice(2, 4);
  const local = digits.slice(4);

  if (local.length === 9) {
    return `+${cc} (${ddd}) ${local.slice(0, 5)}-${local.slice(5)}`;
  }

  if (local.length === 8) {
    return `+${cc} (${ddd}) ${local.slice(0, 4)}-${local.slice(4)}`;
  }

  return `+${cc} ${ddd} ${local}`.trim();
}

export function maskBrazilPhoneInput(value = "") {
  const digits = normalizeBrazilPhone(value);
  if (!digits) return "";
  return formatBrazilPhone(digits);
}

export function parseTemplateVariables(value = "") {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 10);
}

export function formatTemplateVariables(value) {
  return (Array.isArray(value) ? value : []).join(", ");
}

export function ensureArray(value) {
  return Array.isArray(value) ? value : [];
}

export function ensureObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}

export function splitCommaTags(value) {
  return String(value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function renderTemplatePreview(bodyText = "", values = []) {
  const safeBody = String(bodyText || "");
  return safeBody.replace(/\{\{(\d+)\}\}/g, (_, rawIndex) => {
    const index = Number(rawIndex) - 1;
    return values[index] || `{{${rawIndex}}}`;
  });
}

export function renderQuickReplyBody(bodyText = "", context = {}) {
  const safeBody = String(bodyText || "");
  return safeBody.replace(/\{(nome|empresa|telefone|atendente)\}/gi, (_, token) => {
    const key = String(token || "").toLowerCase();
    const dictionary = {
      nome: context.name || "Cliente",
      empresa: context.company || "sua empresa",
      telefone: context.phone || "",
      atendente: context.agent || "Atendimento",
    };
    return dictionary[key] || "";
  });
}

export function extractQuickReplyCommand(value = "") {
  const match = String(value || "").match(/(?:^|\s)\/([a-z0-9_-]{1,40})$/i);
  return match ? match[1].toLowerCase() : "";
}

export function replaceQuickReplyCommand(value = "", rendered = "") {
  const trimmedSnippet = String(rendered || "").trim();
  if (!trimmedSnippet) return String(value || "");
  const source = String(value || "");
  if (!extractQuickReplyCommand(source)) return source.trim() ? `${source.trim()}\n\n${trimmedSnippet}` : trimmedSnippet;
  return source.replace(/(?:^|\s)\/([a-z0-9_-]{1,40})$/i, (match) => {
    const leadingSpace = /^\s/.test(match) ? " " : "";
    return `${leadingSpace}${trimmedSnippet}`;
  }).trim();
}

export const signatureTemplatePresets = [
  { id: "brackets", label: "[ CIRO ] :", template: "[{agent}] :\n\n{message}" },
  { id: "says", label: "Ciro diz:", template: "{agent} diz:\n\n{message}" },
  { id: "simple", label: "Ciro:", template: "{agent}:\n{message}" },
  { id: "custom", label: "Personalizado", template: "" },
];

export function normalizeSignatureTemplate(value = "") {
  const next = String(value || "").trim();
  return next.includes("{message}") ? next : "[{agent}] :\n\n{message}";
}

export function formatSignaturePreview(template, agentName, message) {
  return normalizeSignatureTemplate(template)
    .replace(/\{agent\}/g, agentName || "Ciro")
    .replace(/\{message\}/g, message || "Oi, tudo bem?");
}

export function getInitials(name = "SZ") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "SZ";
}

export function conversationStatusLabel(status) {
  const labels = {
    novo: "Novo",
    atendimento: "Em atendimento",
    finalizado: "Finalizado",
  };
  return labels[status] || status || "Sem status";
}

export function timelineEventTitle(type) {
  const labels = {
    conversation_created: "Conversa iniciada",
    assignment: "Responsável atualizado",
    status_change: "Status alterado",
    lead_created: "Oportunidade criada",
    note: "Nota interna",
    template_sent: "Template enviado",
    quick_reply: "Resposta rápida preparada",
  };
  return labels[type] || "Atualização";
}

export function timelineEventTone(type) {
  const tones = {
    conversation_created: "neutral",
    assignment: "info",
    status_change: "success",
    lead_created: "accent",
    note: "warning",
    template_sent: "accent",
    quick_reply: "info",
  };
  return tones[type] || "neutral";
}

export function windowStatusTone(windowStatus) {
  if (!windowStatus) return "idle";
  if (windowStatus.status === "open" && Number(windowStatus.minutes_remaining || 0) <= 120) return "warning";
  if (windowStatus.status === "open") return "success";
  if (windowStatus.status === "closed") return "critical";
  return "idle";
}

export function companyStatusLabel(status) {
  const labels = {
    active: "Ativa",
    onboarding: "Onboarding",
    suspended: "Suspensa",
    blocked: "Bloqueada",
  };
  return labels[status] || status || "Sem status";
}

export function billingStatusLabel(status) {
  const labels = {
    trial: "Trial",
    active: "Em dia",
    late: "Em atraso",
    blocked: "Bloqueado",
  };
  return labels[status] || status || "Não definido";
}

export function whatsappSessionLabel(status) {
  const labels = {
    connected: "WhatsApp conectado",
    qr_ready: "QR pronto para leitura",
    pairing_code_ready: "Código de pareamento pronto",
    connecting: "Conectando WhatsApp",
    reconnecting: "Reconectando WhatsApp",
    disconnected: "WhatsApp desconectado",
    managed_elsewhere: "Sessão gerenciada em outra instância",
  };
  return labels[status] || "Status desconhecido";
}

export function messageStatusLabel(status) {
  const labels = {
    sent: "enviada",
    delivered: "entregue",
    read: "lida",
    received: "recebida",
    saved: "salva",
    error: "erro",
  };
  return labels[status] || status || "registrada";
}

export function formatMinutesAsLabel(minutes) {
  const total = Number(minutes || 0);
  if (!total) return "agora";
  if (total < 60) return `${total} min`;
  const hours = Math.floor(total / 60);
  const remaining = total % 60;
  if (hours < 24) return remaining ? `${hours}h ${remaining}min` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const extraHours = hours % 24;
  return extraHours ? `${days}d ${extraHours}h` : `${days}d`;
}

export function getSlaMeta(conversation) {
  const minutes = Number(conversation?.waiting_minutes || 0);
  const waiting = Boolean(conversation?.waiting_for_reply);
  if (!waiting || minutes <= 0) return { tone: "idle", label: "Sem fila", detail: "sem pendência do cliente" };
  if (minutes >= 60) return { tone: "critical", label: "Crítico", detail: `aguardando há ${formatMinutesAsLabel(minutes)}` };
  if (minutes >= 15) return { tone: "warning", label: "Atenção", detail: `aguardando há ${formatMinutesAsLabel(minutes)}` };
  return { tone: "healthy", label: "No prazo", detail: `aguardando há ${formatMinutesAsLabel(minutes)}` };
}

export function getConversationPriority(conversation, currentUserId) {
  const sla = getSlaMeta(conversation);
  const unread = Number(conversation?.unread_count || 0);
  const waiting = Boolean(conversation?.waiting_for_reply);
  const waitingMinutes = Number(conversation?.waiting_minutes || 0);
  const isMine = String(conversation?.assigned_to || "") === String(currentUserId || "");
  const unassigned = !conversation?.assigned_to;
  const isNew = conversation?.status === "novo";
  const isFinished = conversation?.status === "finalizado";

  let score = 0;
  if (!isFinished) score += 40;
  if (waiting) score += Math.min(waitingMinutes, 180);
  if (sla.tone === "critical") score += 260;
  if (sla.tone === "warning") score += 170;
  if (sla.tone === "healthy") score += 90;
  if (unread > 0) score += 110 + Math.min(unread, 9) * 7;
  if (unassigned && !isFinished) score += 70;
  if (isNew) score += 35;
  if (isMine && waiting) score += 20;
  if (isFinished) score -= 240;

  let level = "idle";
  let label = "Rotina";
  let summary = "Em acompanhamento normal";

  if (isFinished) {
    label = "Finalizada";
    summary = "Conversa concluída";
  } else if (sla.tone === "critical" || (waiting && unread > 0)) {
    level = "critical";
    label = "Prioridade máxima";
    summary = unread > 0 ? "Cliente esperando e ainda não lido" : "Cliente aguardando resposta";
  } else if (sla.tone === "warning" || unassigned || isNew) {
    level = "warning";
    label = unassigned ? "Precisa dono" : "Fila ativa";
    summary = unassigned ? "Assuma ou distribua esta conversa" : (isNew ? "Nova conversa para triagem" : "Responder mantém o SLA saudável");
  } else if (waiting || unread > 0) {
    level = "active";
    label = "Em curso";
    summary = waiting ? "Acompanhar resposta do time" : "Há atividade recente nessa conversa";
  }

  return { score, level, label, summary, unread };
}
